import React, { ReactElement, useCallback, useState } from "react"
import "./global-notification.css"
import classNames from "classnames"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"
import { zIndex } from "@nova/constants"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

export enum GlobalNotificationStatus {
    success = "success",
    warning = "warning",
    error = "error",
    default = "default",
    basic = "basic",
    info = "info",
}
export enum GlobalNotificationActionsNames {
    CLONE_STATE = "Clone State",
    UNSUBSCRIBE_FROM_MAP = "Unsubscribe From Map",
    AUTH_EXPIRED = "Auth Expired",
}

export enum GlobalNotificationSecondaryComponentNames {
    COPY_BUTTON = "copy_button",
}

export type GlobalNotificationActions = Record<
    GlobalNotificationActionsNames,
    (context?: Record<string, string>) => void
>

export type GlobalNotificationSecondaryComponents = Record<
    GlobalNotificationSecondaryComponentNames,
    () => ReactElement
>

export type GlobalNotificationContext = {
    message: string
    status: GlobalNotificationStatus
    timeoutInMs?: number
    timeoutAction?: { type: GlobalNotificationActionsNames; context?: Record<string, string> }
    secondaryComponentName?: GlobalNotificationSecondaryComponentNames
    color?: string
}

type GlobalNotificationProps = {
    context: GlobalNotificationContext
    actions: GlobalNotificationActions
    secondaryComponents: GlobalNotificationSecondaryComponents
}

export const GlobalNotification = ({
    context,
    actions,
    secondaryComponents,
}: GlobalNotificationProps) => {
    const { t } = useTranslation()
    const [timeoutProgress, setTimeoutProgress] = useState("100%")
    const [active, setActive] = useState(false)
    const [notification, setNotification] = useState(context)

    useEffectUnsafe(() => {
        if (context.message) {
            setActive(true)
            setTimeoutProgress("100%")

            setNotification(context)
            if (context.timeoutInMs) {
                const timeoutInMs = context.timeoutInMs
                const timeoutAction = context.timeoutAction
                const progressTimer = setTimeout(() => {
                    setTimeoutProgress("0%")
                }, 0)

                const notificationTimer = setTimeout(() => {
                    clearTimeout(notificationTimer)
                    closeNotificationHandler()
                    if (timeoutAction) {
                        actions[timeoutAction.type](timeoutAction.context)
                    }
                }, timeoutInMs)

                return () => {
                    clearTimeout(progressTimer)
                    clearTimeout(notificationTimer)
                }
            }
            return
        }
        setActive(false)
    }, [context])

    const closeNotificationHandler = useCallback(() => {
        setActive(false)
        setTimeoutProgress("100%")
    }, [])

    const { message, status, color, secondaryComponentName } = notification
    const SecondaryComponent = secondaryComponentName && secondaryComponents[secondaryComponentName]
    const Icon =
        status === GlobalNotificationStatus.success
            ? Icons.Checkmark
            : status === GlobalNotificationStatus.error
              ? Icons.Error
              : status === GlobalNotificationStatus.warning
                ? Icons.Warning
                : Icons.Information
    return (
        <div
            className={classNames(
                `global-notification global-notification--${status} overflow-hidden`,
                {
                    "global-notification--active": active,
                }
            )}
            style={{
                backgroundColor: color,
                zIndex: zIndex.GlobalNotification,
            }}
            data-testid={`global-notification-${active}`}
        >
            <div
                className="global-notification__message"
                data-testid={`global-notification-${status}`}
            >
                {status !== GlobalNotificationStatus.basic && <Icons.SmallIcon Icon={Icon} />}

                <h4 className="global-notification__status-message text-gray-0">{t(message)}</h4>

                {SecondaryComponent && (
                    <div onClick={() => closeNotificationHandler()}>
                        <SecondaryComponent />
                    </div>
                )}
                <Icons.SmallIcon
                    Icon={Icons.XClose}
                    className="text-white-400 hover:text-white-700 cursor-pointer"
                    onClick={() => {
                        //fire off the timeout action on close if it's a static notification
                        if (!notification.timeoutInMs && notification.timeoutAction?.type) {
                            actions[notification.timeoutAction?.type](
                                notification.timeoutAction?.context
                            )
                        }
                        closeNotificationHandler()
                    }}
                    data-testid="global-notification-close-button"
                />
            </div>
            <div
                className="global-notification__timer transition-all"
                style={{
                    width: timeoutProgress,
                    transitionDuration:
                        timeoutProgress !== "100%" ? `${context.timeoutInMs}ms` : undefined,
                }}
            />
        </div>
    )
}
